import {
  IssueType,
  SingleIssueType,
  IssueTypeDataT,
  IssueTypesOrder,
} from '@/routes/settings/issueTypes/types/issueType.ts'
import { api, queryClient } from '@/lib/api-client.ts'
import { AxiosResponse, AxiosError } from 'axios'
import { getCurrentOrganization } from '@/lib/utils.ts'
import useIssueTypeStore from '@/routes/settings/issueTypes/state/issueTypesStore.ts'
import { subscribeToMercureTopic } from '@/lib/helpers/subscribeToMercureTopic.ts'
import type { MercureSubscription } from '@/lib/helpers/subscribeToMercureTopic.ts'
import createIri from '@/lib/helpers/createIri.ts'
import { settingsQueryKeys } from '@/lib/queryKeys.ts'
import { settingsScope } from '@/lib/settingsScope.ts'

type QuickEditData = {
  name: string | undefined
  color: string | undefined
}

const issueTypeSubscriptions = new Map<string, Map<string, MercureSubscription>>()

function handleIssueTypeUpdate(queryKey: unknown[], event: MessageEvent): void {
  let data: SingleIssueType
  try {
    data = JSON.parse(event.data) as SingleIssueType
  } catch {
    return
  }

  const issueTypesList = queryClient.getQueryData<IssueType>(queryKey)
  if (!issueTypesList) return

  const updatedMembers = issueTypesList['hydra:member'].map((issue: SingleIssueType) =>
    issue.id === data.id ? data : issue,
  )
  queryClient.setQueryData(queryKey, { 'hydra:member': updatedMembers })
  useIssueTypeStore.getState().setIssueTypesList({ 'hydra:member': updatedMembers })
}

/**
 * `topicPrefix` is passed in rather than derived from `scope`, because it is not a formatting of
 * the same fact: the publisher roots each entity topic under the group's own scope
 * (SettingsListener::scopedEntityTopic), since /organizations/{o}/{+rest} and /projects/{p}/{+rest}
 * are the only roots an ordinary subscriber's claims carry. `scope` keys the subscription map and
 * would still read correctly after the root changed underneath it.
 */
function syncIssueTypeSubscriptions(
  scope: string,
  topicPrefix: string,
  queryKey: unknown[],
  members: SingleIssueType[],
): void {
  const previous = issueTypeSubscriptions.get(scope) ?? new Map()
  const next = new Map<string, MercureSubscription>()

  members.forEach(issueType => {
    const topic = `${topicPrefix}${createIri('issue_types', issueType.id)}`
    const existing = previous.get(topic)
    if (existing && !existing.closed) {
      next.set(topic, existing)
      return
    }

    next.set(
      topic,
      subscribeToMercureTopic(topic, event => handleIssueTypeUpdate(queryKey, event), {
        onRecovery: () => {
          void queryClient.invalidateQueries({ queryKey })
        },
      }),
    )
  })

  previous.forEach((subscription, topic) => {
    if (!next.has(topic)) {
      subscription.close()
    }
  })
  issueTypeSubscriptions.set(scope, next)
}

export const organizationIssueTypesQuery = ({ projectId }: { projectId: string | undefined }) => ({
  queryKey: settingsQueryKeys.issueTypes(settingsScope(projectId)),
  queryFn: () => (projectId ? getProjectIssueTypes(projectId) : getIssueTypes()),
})

export const getOrganizationIssueTypes = async ({
  projectId = undefined,
}: {
  projectId: string | undefined
  view?: string
}) => {
  return await queryClient.ensureQueryData(organizationIssueTypesQuery({ projectId }))
}

export const getProjectIssueTypes = async (projectId: string | undefined) => {
  const { setIssueTypesList } = useIssueTypeStore.getState()
  if (!projectId) {
    return null
  }

  try {
    const response = await api.get(`/projects/${projectId}/issue_types`)
    const issueTypes = response.data as IssueType
    if (issueTypes) {
      setIssueTypesList(issueTypes)
      syncIssueTypeSubscriptions(
        `project:${projectId}`,
        `/projects/${projectId}`,
        settingsQueryKeys.issueTypes({ projectId }),
        issueTypes['hydra:member'],
      )
    }

    return issueTypes
  } catch (error) {
    if (error instanceof AxiosError) {
      return null
    }
  }
  return null
}
export const getIssueTypes = async () => {
  const organizationId = getCurrentOrganization()?.organization?.id
  const { setIssueTypesList } = useIssueTypeStore.getState()

  if (!organizationId) {
    return null
  }

  try {
    const response = await api.get(`/organizations/${organizationId}/issue_types`)
    const issueTypes = response.data as IssueType
    if (issueTypes) {
      setIssueTypesList(issueTypes)
      syncIssueTypeSubscriptions(
        `organization:${organizationId}`,
        `/organizations/${organizationId}`,
        settingsQueryKeys.issueTypes({ organizationId }),
        issueTypes['hydra:member'],
      )
    }

    return issueTypes
  } catch (error) {
    if (error instanceof AxiosError) {
      return null
    }
  }
  return null
}

export const createIssueType = async (
  newIssueTypeData: IssueTypeDataT,
): Promise<AxiosResponse<SingleIssueType>> => {
  return api.post('/issue_types', newIssueTypeData)
}

export const quickEditIssueType = async (
  issueType: SingleIssueType,
  quickEditData: QuickEditData,
): Promise<AxiosResponse<SingleIssueType>> => {
  return api.patch(`/issue_types/${issueType.id}`, {
    ...quickEditData,
  })
}

export const editIssueType = async (
  issueType: IssueTypeDataT,
): Promise<AxiosResponse<SingleIssueType>> => {
  return api.patch(`/issue_types/${issueType.id}`, issueType)
}

export const updateIssueTypeOrdering = async (
  submitData: {
    issueTypes: IssueTypesOrder[]
  },
  projectId: string | undefined = undefined,
): Promise<AxiosResponse<SingleIssueType>> => {
  if (projectId) {
    return api.patch(`/projects/${projectId}/issue_types/`, submitData)
  }
  const organizationId = getCurrentOrganization()?.organization?.id
  return api.patch(`/organizations/${organizationId}/issue_types/`, submitData)
}

export const deleteIssueType = async (
  issueType: SingleIssueType,
): Promise<AxiosResponse<SingleIssueType>> => {
  return api.delete(`/issue_types/${issueType.id}`)
}
